import React, { useState } from "react";
import { useTranslation, withTranslation } from "react-i18next";

import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import FormBox from "../../component/FormBox/FormBox";
import {
    Box,
    Button,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
} from "@material-ui/core";
import errorWarning from "../../images/icon_warning.gif";
import { isEmpty, isNotEmpty } from "../../Utils";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentStep, setFieldValues } from "../../redux/action";
import { getFieldValuesByStep, getFormId } from "../../redux/selector";
import CustomDatePicker from "../../component/CustomDatePicker/CustomDatePicker";
import moment from "moment";

export default function DatePickerSample() {
    const formId = useSelector(getFormId());
    const stepName = "DatePicker";

    const { t, i18n } = useTranslation();

    const maximumSignature = 2;

    const rewardOption = t(`${formId}.label.rewardOption`, {
        returnObjects: true,
    });
    const dispatch = useDispatch();
    let defaultValues = useSelector(getFieldValuesByStep(stepName));
    const initialDefaultValues = {
        birthDate: null,
        birthMonth: null,
        birthPureMonth: "",
        birthYear: null,
        birthPureYear: null,
        birthDateTime: new Date(),
        noLabel: null,
        withMinDate: null,
        withMaxDate: null,
        withDropdown: null,
    };
    if (isEmpty(defaultValues)) {
        defaultValues = initialDefaultValues;
    }
    const yupSchema = yup.object().shape({
        birthDate: yup
            .date()
            .nullable()
            .test("Date is required", t("errorMsg.required"), (value) =>
                isNotEmpty(value)
            )
            .typeError(t("errorMsg.date.rule")),
        birthMonth: yup
            .date()
            .nullable()
            .test("Date is required", t("errorMsg.required"), (value) =>
                isNotEmpty(value)
            )
            .typeError(t("errorMsg.date.rule")),
        birthPureMonth: yup.string().required(t("errorMsg.required")),
        birthYear: yup
            .date()
            .min(new Date())
            .nullable()
            .test("Date is required", t("errorMsg.required"), (value) =>
                isNotEmpty(value)
            )
            .typeError(t("errorMsg.date.rule")),
        birthPureYear: yup
            .number()
            .nullable()
            .test("Date is required", t("errorMsg.required"), (value) =>
                isNotEmpty(value)
            )
            .typeError(t("errorMsg.date.rule")),
        birthDateTime: yup
            .date()
            .nullable()
            .test("Date is required", t("errorMsg.required"), (value) =>
                isNotEmpty(value)
            )
            .typeError(t("errorMsg.date.rule")),
        noLabel: yup
            .date()
            .nullable()
            .test("Date is required", t("errorMsg.required"), (value) =>
                isNotEmpty(value)
            )
            .typeError(t("errorMsg.date.rule")),
        withMinDate: yup
            .date()
            .nullable()
            .test("Date is required", t("errorMsg.required"), (value) =>
                isNotEmpty(value)
            )
            .typeError(t("errorMsg.date.rule")),
        withMaxDate: yup
            .date()
            .nullable()
            .test("Date is required", t("errorMsg.required"), (value) =>
                isNotEmpty(value)
            )
            .typeError(t("errorMsg.date.rule")),
        withDropdown: yup
            .date()
            .nullable()
            .test("Date is required", t("errorMsg.required"), (value) =>
                isNotEmpty(value)
            )
            .typeError(t("errorMsg.date.rule")),
    });

    const {
        register,
        handleSubmit,
        control,
        formState,
        clearErrors,
        reset,
        watch,
        getValues,
        setValue,
    } = useForm({
        defaultValues,
        mode: "onBlur",
        resolver: yupResolver(yupSchema),
    });

    const onStepSubmit = (formData) => {
        //onStepSubmit
        console.log("onStepSubmit, formData:", formData);
        dispatch(setFieldValues(stepName, formData));
        dispatch(setCurrentStep("Summary"));
    };

    const onStepError = (errors) => {
        //onStepError
        console.log("onStepError, errors: ", errors);
    };

    const onPrev = () => {};

    const onReset = () => {
        reset(initialDefaultValues);
    };

    const errors = formState.errors;

    return (
        <form noValidate>
            <FormBox header="DatePicker">
                <Box p={5}>
                    <Box mb={6}>
                        <CustomDatePicker
                            label={t("some_translation.key", "出生日期")}
                            control={control}
                            name="birthDate"
                        />
                    </Box>
                    <Box mb={6}>
                        <CustomDatePicker
                            label={t("some_translation.key", "出生月份")}
                            control={control}
                            name="birthMonth"
                            type="month"
                        />
                    </Box>
                    <Box mb={6}>
                        <CustomDatePicker
                            label={t(
                                "some_translation.key",
                                "出生月份(accept and return a string instead of a Date object)"
                            )}
                            control={control}
                            name="birthPureMonth"
                            type="pureMonth"
                        />
                    </Box>
                    <Box mb={6}>
                        <CustomDatePicker
                            label={t("some_translation.key", "出生年份")}
                            control={control}
                            name="birthYear"
                            type="year"
                        />
                    </Box>
                    <Box mb={6}>
                        <CustomDatePicker
                            label={t(
                                "some_translation.key",
                                "出生年份(accept and return a number instead of a Date object)"
                            )}
                            control={control}
                            name="birthPureYear"
                            type="pureYear"
                        />
                    </Box>
                    <Box mb={6}>
                        <CustomDatePicker
                            label={t("some_translation.key", "出生日期連時間")}
                            control={control}
                            name="birthDateTime"
                            type="dateTime"
                        />
                    </Box>
                    <Box mb={6}>
                        This is a date picker without form label
                        <CustomDatePicker control={control} name="noLabel" />
                    </Box>
                    <Box mb={6}>
                        <CustomDatePicker
                            label={t(
                                "some_translation.key",
                                "最小可選擇日期為今天"
                            )}
                            control={control}
                            name="withMinDate"
                            type="day"
                            minDate={
                                moment().toDate() /*set min date as today*/
                            }
                        />
                    </Box>
                    <Box mb={6}>
                        <CustomDatePicker
                            label={t(
                                "some_translation.key",
                                "最大可選擇日期為5天後"
                            )}
                            control={control}
                            name="withMaxDate"
                            type="day"
                            maxDate={
                                moment()
                                    .add(5, "days")
                                    .toDate() /*set max date as 5 days after today*/
                            }
                        />
                    </Box>
                    <Box mb={6}>
                        <CustomDatePicker
                            label={t(
                                "some_translation.key",
                                "顯示月份年份下拉式選單"
                            )}
                            control={control}
                            name="withDropdown"
                            type="day"
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                        />
                    </Box>
                </Box>
            </FormBox>
            <FormBox>
                <Box display="flex" justifyContent="flex-end">
                    <Button
                        variant="contained"
                        color="secondary"
                        className="secBtn"
                        onClick={() => onPrev()}
                    >
                        {t("common.button.back")}
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        className="secBtn"
                        onClick={() => onReset()}
                    >
                        {t("common.button.clear")}
                    </Button>
                    <Button variant="contained" color="primary" type="submit">
                        {t("common.button.next")}
                    </Button>
                </Box>
            </FormBox>
        </form>
    );
}
